/**
 * The alerts above the sign-in fields in `LoginForm.tsx`: an info message passed in by the page,
 * and the sign-in error.
 */
import React from "react";
import { Alert } from "@ryerson/frontend.notification";
import { Typography } from "@ryerson/frontend.typography";
import { Flex } from "@ryerson/frontend.layout";
import { Link } from "@ryerson/frontend.navigation";
import { useApplication } from "@ryerson/frontend.application";
import { CONSTANTS } from "@ryerson/frontend.common";
import LockOutErrorMessage from "@components/Shared/Password/LockOutErrorMessage";
import ResetErrorMessage from "@components/Shared/Password/ResetErrorMessage";
import { LoginErrorResponse } from "@enums/common.enums";
import { LoginContent } from "./LoginContent";

interface LoginAlertMessageProps {
	message?: string;
	severity: "info" | "error";
	isMobile: boolean;
}

/** Shows `message` in an outlined alert of the given `severity`; renders nothing without one. */
export const LoginAlertMessage: React.FC<LoginAlertMessageProps> = ({
	message,
	severity,
	isMobile,
}) => {
	if (!message) {
		return null;
	}

	return (
		<Flex css={{ marginTop: isMobile ? 0 : "36px", marginBottom: "10px" }}>
			<Alert severity={severity} size="sm" outline>
				<Typography type="primary" size="sm">
					{message}
				</Typography>
			</Alert>
		</Flex>
	);
};

interface LoginErrorMessageProps {
	errorType: "validationError" | "apiError" | null;
	apiErrorMessage?: string;
	isMobile: boolean;
}

/**
 * The sign-in error, by `errorType`:
 * - `"validationError"` (a field is empty): `validationErrorMessage`;
 * - `"apiError"` with `apiErrorMessage` equal to `LoginErrorResponse.ContactSupport` (the Users
 *   API's 423, account locked): `Shared/Password/LockOutErrorMessage.tsx`;
 * - `"apiError"` with `LoginErrorResponse.Reset` (the 403, password reset required):
 *   `Shared/Password/ResetErrorMessage.tsx`;
 * - any other `"apiError"`: `CONSTANTS[language].AUTH_ERROR` from `@ryerson/frontend.common`,
 *   with a link to `/login/forgot-password`.
 * The two special cases depend on `LoginForm.tsx` → `onLoginClick` passing the API's response
 * text; see that function for what it passes.
 */
export const ErrorMessage: React.FC<LoginErrorMessageProps> = ({
	errorType,
	apiErrorMessage,
	isMobile,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const content = LoginContent[language];

	if (!errorType) {
		return null;
	}

	// Handle special API error cases
	if (errorType === "apiError" && apiErrorMessage === LoginErrorResponse.ContactSupport) {
		return <LockOutErrorMessage />;
	}

	if (errorType === "apiError" && apiErrorMessage === LoginErrorResponse.Reset) {
		return <ResetErrorMessage />;
	}

	// Validation error - show generic validation message
	if (errorType === "validationError") {
		return (
			<Flex css={{ marginTop: "24px", marginBottom: "24px" }}>
				<Alert severity="error" size="sm" outline>
					<Typography type="primary" size="sm" autoResize={false}>
						{content.validationErrorMessage}
					</Typography>
				</Alert>
			</Flex>
		);
	}

	// API error - show AUTH_ERROR with reset password link
	if (errorType === "apiError") {
		return (
			<Flex css={{ marginTop: isMobile ? 0 : "24px", marginBottom: isMobile ? 0 : "24px" }}>
				<Alert severity="error" size="sm" outline fullWidth>
					<Typography type="primary" size="sm">
						{CONSTANTS[language].AUTH_ERROR}{" "}
						<Link type="secondary" to="/login/forgot-password" size="sm">
							{content.resetPasswordText}
						</Link>
					</Typography>
				</Alert>
			</Flex>
		);
	}

	return null;
};
